import { useState, useEffect, useRef, useCallback } from 'react';
import InformationOverlay from './InformationOverlay';

interface Artwork {
  id: number;
  title: string;
  artist: string;
  year: string;
  medium: string;
  dimensions: string;
  imageUrl: string;
  description: string;
  location: string;
  sort_order: number;
}

type WingKey = 'north' | 'east' | 'south' | 'west' | 'main';

interface WingRoomProps {
  wing: WingKey;
  artworks: Artwork[];
  lightIntensity: number;
  onArtworkClick: (id: number) => void;
  onNavigateWing: (wing: WingKey) => void;
}

const wingOrder: WingKey[] = ['main', 'north', 'east', 'south', 'west'];

const wingConfig: Record<WingKey, { name: string; subtitle: string }> = {
  main:  { name: 'Main Gallery',   subtitle: 'Contemporary Collection' },
  north: { name: 'Gallery Room 1', subtitle: 'Contemporary Collection' },
  east:  { name: 'Gallery Room 2', subtitle: 'Contemporary Collection' },
  south: { name: 'Gallery Room 3', subtitle: 'Contemporary Collection' },
  west:  { name: 'Gallery Room 4', subtitle: 'Contemporary Collection' },
};

// ─── Walk-Up Zoom Overlay ─────────────────────────────────────────────────────
function WalkUpZoom({ artwork, onComplete }: { artwork: Artwork; onComplete: () => void }) {
  const [phase, setPhase] = useState<'zoom' | 'hold'>('zoom');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('hold'), 600);
    const t2 = setTimeout(() => onComplete(), 950);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onComplete]);

  return (
    <div
      className="fixed inset-0"
      style={{
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: phase === 'hold' ? 'rgba(255,255,255,0.97)' : 'rgba(255,255,255,0)',
        transition: 'background 0.35s ease',
        pointerEvents: 'none',
      }}
    >
      <div style={{
        transform: phase === 'zoom' ? 'scale(0.55)' : 'scale(1.0)',
        opacity: phase === 'zoom' ? 0.4 : 1,
        transition: 'transform 0.6s cubic-bezier(0.22,1,0.36,1), opacity 0.5s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <div style={{
          width: 360,
          height: 440,
          background: '#1A1A1A',
          padding: 14,
          boxShadow: '0 40px 100px rgba(0,0,0,0.25)',
        }}>
          <div style={{
            width: '100%', height: '100%',
            background: '#fff',
            padding: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <img
              src={artwork.imageUrl}
              alt={artwork.title}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </div>
        </div>
        <div style={{
          marginTop: 16,
          background: 'rgba(255,255,255,0.95)',
          padding: '8px 20px',
          borderRadius: 2,
          border: '1px solid rgba(0,0,0,0.08)',
          textAlign: 'center',
        }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1A1A1A', fontWeight: 400 }}>
            {artwork.title}
          </p>
          <p style={{ fontSize: 11, color: '#888', fontWeight: 300, marginTop: 3 }}>
            {artwork.artist}, {artwork.year}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Single Painting on Wall ──────────────────────────────────────────────────
function WallPainting({
  artwork,
  maxWidth,
  maxHeight,
  onClick,
}: {
  artwork: Artwork;
  maxWidth: number;
  maxHeight: number;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    setDims(null);
    const img = new Image();
    img.onload = () => {
      const ratio = img.naturalWidth / img.naturalHeight;
      let w = maxWidth;
      let h = maxWidth / ratio;
      if (h > maxHeight) {
        h = maxHeight;
        w = maxHeight * ratio;
      }
      setDims({ w: Math.round(w), h: Math.round(h) });
    };
    img.src = artwork.imageUrl;
  }, [artwork.imageUrl, maxWidth, maxHeight]);

  if (!dims) return null;

  const { w: width, h: height } = dims;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width,
        height,
        flexShrink: 0,
        cursor: 'pointer',
        transform: hovered ? 'translateY(-6px) scale(1.025)' : 'translateY(0) scale(1)',
        transition: 'transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94)',
        position: 'relative',
      }}
    >
      {/* Spotlight glow */}
      <div style={{
        position: 'absolute',
        top: -50,
        left: '50%',
        transform: 'translateX(-50%)',
        width: width + 60,
        height: height + 80,
        background: `radial-gradient(ellipse at 50% 0%, rgba(255,255,240,${hovered ? 0.55 : 0.3}) 0%, transparent 65%)`,
        filter: 'blur(18px)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />

      {/* Canvas */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        height: '100%',
        boxShadow: hovered
          ? '0 28px 70px rgba(0,0,0,0.28), 0 8px 20px rgba(0,0,0,0.16)'
          : '0 10px 32px rgba(0,0,0,0.16), 0 3px 10px rgba(0,0,0,0.10)',
        transition: 'box-shadow 0.4s ease',
        overflow: 'hidden',
      }}>
        <img
          src={artwork.imageUrl}
          alt={artwork.title}
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'fill',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        />
        {/* Hover overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.36)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}>
          <i className="ri-zoom-in-line" style={{ color: 'white', fontSize: 26 }} />
          <span style={{
            color: 'white',
            fontSize: 9,
            letterSpacing: '2px',
            textTransform: 'uppercase',
            fontWeight: 300,
          }}>
            Walk Up
          </span>
        </div>
      </div>

      {/* Wall label below painting */}
      <div style={{
        position: 'absolute',
        bottom: -38,
        left: '50%',
        transform: 'translateX(-50%)',
        textAlign: 'center',
        whiteSpace: 'nowrap',
      }}>
        <p style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 10,
          color: '#2A2A2A',
          fontWeight: 400,
          letterSpacing: '0.3px',
        }}>
          {artwork.title}
        </p>
        <p style={{ fontSize: 9, color: '#888', fontWeight: 300, marginTop: 2 }}>
          {artwork.artist} · {artwork.year}
        </p>
      </div>
    </div>
  );
}

// ─── Gallery Room View ────────────────────────────────────────────────────────
function GalleryRoomView({
  artworks,
  lightMult,
  onPaintingClick,
  onNavigateNext,
  onNavigatePrev,
  nextWing,
  prevWing,
  walkingForward,
  walkDirection,
}: {
  artworks: Artwork[];
  lightMult: number;
  onPaintingClick: (artwork: Artwork) => void;
  onNavigateNext: () => void;
  onNavigatePrev: () => void;
  nextWing: WingKey;
  prevWing: WingKey;
  walkingForward: boolean;
  walkDirection: 'prev' | 'next' | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const lastX = useRef(0);
  const [panX, setPanX] = useState(0);
  const [showHint, setShowHint] = useState(true);

  // Painting dimensions
  const PAINT_W = 200;
  const PAINT_H = 260;
  const GAP = 96;
  const WALL_PADDING = 80;

  // Distribute artworks across 3 walls: left, front, right
  const leftArtworks  = artworks.filter((_, i) => i % 3 === 0);
  const frontArtworks = artworks.filter((_, i) => i % 3 === 1);
  const rightArtworks = artworks.filter((_, i) => i % 3 === 2);

  const brightness = 0.88 + lightMult * 0.12;

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    lastX.current = e.clientX;
    setShowHint(false);
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - lastX.current;
    lastX.current = e.clientX;
    setPanX(prev => Math.max(-600, Math.min(600, prev + dx * 0.9)));
  }, []);

  const onMouseUp = useCallback(() => { isDragging.current = false; }, []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    isDragging.current = true;
    lastX.current = e.touches[0].clientX;
    setShowHint(false);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const dx = e.touches[0].clientX - lastX.current;
    lastX.current = e.touches[0].clientX;
    setPanX(prev => Math.max(-600, Math.min(600, prev + dx * 0.9)));
  }, []);

  const onTouchEnd = useCallback(() => { isDragging.current = false; }, []);

  const renderWallRow = (wallArtworks: Artwork[], wallLabel: string) => {
    const totalW = wallArtworks.length * (PAINT_W + GAP) - GAP + WALL_PADDING * 2;
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: Math.max(totalW, 400),
      }}>
        {/* Wall surface */}
        <div style={{
          background: `linear-gradient(180deg, #F8F8F8 0%, #FFFFFF 40%, #F5F5F5 100%)`,
          filter: `brightness(${brightness})`,
          borderRadius: 2,
          padding: `60px ${WALL_PADDING}px 70px`,
          display: 'flex',
          alignItems: 'flex-end',
          gap: GAP,
          boxShadow: 'inset 0 0 60px rgba(0,0,0,0.04), 0 2px 20px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.05)',
          position: 'relative',
          minHeight: PAINT_H + 160,
        }}>
          {/* Baseboard */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 12,
            background: 'linear-gradient(180deg, #E8E8E8 0%, #D8D8D8 100%)',
            borderTop: '1px solid rgba(0,0,0,0.08)',
          }} />
          {/* Picture rail */}
          <div style={{
            position: 'absolute',
            top: 18,
            left: 0,
            right: 0,
            height: 6,
            background: 'linear-gradient(180deg, #E0E0E0 0%, #D0D0D0 100%)',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }} />

          {wallArtworks.map((artwork) => (
            <WallPainting
              key={artwork.id}
              artwork={artwork}
              maxWidth={PAINT_W}
              maxHeight={PAINT_H}
              onClick={() => onPaintingClick(artwork)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden select-none"
      style={{
        background: 'linear-gradient(180deg, #F0F0F0 0%, #FFFFFF 30%, #FFFFFF 70%, #EBEBEB 100%)',
        cursor: isDragging.current ? 'grabbing' : 'grab',
        filter: `brightness(${brightness})`,
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Ceiling */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: '18%',
        background: 'linear-gradient(180deg, #E8E8E8 0%, #F5F5F5 100%)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }} />

      {/* Track lighting strip */}
      <div style={{
        position: 'absolute',
        top: '17%', left: '10%', right: '10%',
        height: 8,
        background: 'linear-gradient(90deg, transparent, #D0D0D0 10%, #C8C8C8 50%, #D0D0D0 90%, transparent)',
        borderRadius: 4,
      }} />

      {/* Light cones from track */}
      {[20, 35, 50, 65, 80].map((pct) => (
        <div key={pct} style={{
          position: 'absolute',
          top: '17%',
          left: `${pct}%`,
          transform: 'translateX(-50%)',
          width: 180,
          height: '35%',
          background: `radial-gradient(ellipse at 50% 0%, rgba(255,255,220,${0.12 * lightMult}) 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
      ))}

      {/* Floor */}
      <div style={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        height: '22%',
        background: 'linear-gradient(0deg, #D8D8D8 0%, #E8E8E8 40%, #F0F0F0 100%)',
        borderTop: '1px solid rgba(0,0,0,0.07)',
      }} />

      {/* Floor reflection */}
      <div style={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        height: '22%',
        background: 'linear-gradient(0deg, rgba(255,255,255,0.3) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />

      {/* Perspective walls container */}
      <div style={{
        position: 'absolute',
        top: '18%',
        bottom: '22%',
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        perspective: '1200px',
        perspectiveOrigin: '50% 50%',
      }}>
        {/* Scrollable walls strip */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          transform: `translateX(${panX}px) ${walkingForward ? 'scale(1.18)' : 'scale(1)'}`,
          transition: walkingForward
            ? 'transform 0.75s cubic-bezier(0.4,0,0.2,1)'
            : isDragging.current ? 'none' : 'transform 0.08s linear',
          filter: walkingForward ? 'blur(6px)' : 'none',
          transformOrigin: walkDirection === 'prev' ? '20% 50%' : '80% 50%',
        }}>

          {/* Left wall — only render if it has artworks */}
          {leftArtworks.length > 0 && (
            <div style={{
              transform: 'rotateY(32deg)',
              transformOrigin: 'right center',
              marginRight: -20,
              opacity: 0.82,
            }}>
              {renderWallRow(leftArtworks, 'West Wall')}
            </div>
          )}

          {/* Front wall — only render if it has artworks */}
          {frontArtworks.length > 0 && (
            <div style={{ zIndex: 2 }}>
              {renderWallRow(frontArtworks, 'Front Wall')}
            </div>
          )}

          {/* Right wall — only render if it has artworks */}
          {rightArtworks.length > 0 && (
            <div style={{
              transform: 'rotateY(-32deg)',
              transformOrigin: 'left center',
              marginLeft: -20,
              opacity: 0.82,
            }}>
              {renderWallRow(rightArtworks, 'East Wall')}
            </div>
          )}

          {/* Empty room fallback */}
          {artworks.length === 0 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
              padding: '60px 80px',
              background: 'linear-gradient(180deg, #F8F8F8 0%, #FFFFFF 40%, #F5F5F5 100%)',
              border: '1px solid rgba(0,0,0,0.05)',
              borderRadius: 2,
              minWidth: 400,
              minHeight: PAINT_H + 160,
            }}>
              <i className="ri-image-line" style={{ fontSize: 40, color: '#CCC' }} />
              <p style={{ color: '#AAA', fontSize: 13, fontWeight: 300, letterSpacing: '1px', textTransform: 'uppercase' }}>
                No artworks in this room
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Left edge vignette */}
      <div className="absolute inset-y-0 left-0 pointer-events-none" style={{
        width: '12%',
        background: 'linear-gradient(90deg, rgba(220,220,220,0.7) 0%, transparent 100%)',
        zIndex: 5,
      }} />
      {/* Right edge vignette */}
      <div className="absolute inset-y-0 right-0 pointer-events-none" style={{
        width: '12%',
        background: 'linear-gradient(270deg, rgba(220,220,220,0.7) 0%, transparent 100%)',
        zIndex: 5,
      }} />

      {/* Navigation arrows */}
      <button
        onClick={(e) => { e.stopPropagation(); onNavigatePrev(); }}
        className="absolute whitespace-nowrap"
        style={{
          left: 24, top: '50%', transform: 'translateY(-50%)',
          zIndex: 20,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: '50%',
          width: 52, height: 52,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          transition: 'all 0.25s ease',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,1)';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 28px rgba(0,0,0,0.16)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.92)';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
        }}
      >
        <i className="ri-arrow-left-line" style={{ fontSize: 20, color: '#1A1A1A' }} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onNavigateNext(); }}
        className="absolute whitespace-nowrap"
        style={{
          right: 24, top: '50%', transform: 'translateY(-50%)',
          zIndex: 20,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: '50%',
          width: 52, height: 52,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          transition: 'all 0.25s ease',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,1)';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 28px rgba(0,0,0,0.16)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.92)';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
        }}
      >
        <i className="ri-arrow-right-line" style={{ fontSize: 20, color: '#1A1A1A' }} />
      </button>

      {/* Room labels on nav arrows */}
      <div style={{
        position: 'absolute', left: 84, top: '50%', transform: 'translateY(-50%)',
        zIndex: 20, pointerEvents: 'none',
      }}>
      </div>
      <div style={{
        position: 'absolute', right: 84, top: '50%', transform: 'translateY(-50%)',
        zIndex: 20, pointerEvents: 'none',
      }}>
      </div>

      {/* Drag hint */}
      {showHint && (
        <div className="absolute pointer-events-none" style={{
          bottom: '28%', left: '50%', transform: 'translateX(-50%)',
          zIndex: 20,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(12px)',
            padding: '10px 22px', borderRadius: 999,
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}>
            <i className="ri-drag-move-line" style={{ color: '#555', fontSize: 16 }} />
            <span style={{ color: '#555', fontSize: 11, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 400 }}>
              Drag to pan · Click painting to walk up
            </span>
          </div>
        </div>
      )}

      {/* Walk-forward overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 18,
          background: walkingForward
            ? 'radial-gradient(ellipse at center, transparent 0%, rgba(255,255,255,0.0) 30%, rgba(255,255,255,0.95) 100%)'
            : 'transparent',
          transition: walkingForward ? 'background 0.75s ease' : 'none',
        }}
      />
    </div>
  );
}

// ─── Top Navigation Bar ───────────────────────────────────────────────────────
function TopNavBar({ currentWing, onNavigateWing, onBackToGallery }: {
  currentWing: WingKey;
  onNavigateWing: (w: WingKey) => void;
  onBackToGallery: () => void;
}) {
  return (
    <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-8" style={{
      height: 64, zIndex: 45,
      background: 'rgba(255,255,255,0.96)',
      backdropFilter: 'blur(12px)',
      borderBottom: '1px solid rgba(0,0,0,0.08)',
    }}>
      <button
        onClick={onBackToGallery}
        className="flex items-center gap-2 whitespace-nowrap transition-all hover:bg-black/5"
        style={{
          background: 'transparent',
          border: '1px solid rgba(0,0,0,0.15)',
          borderRadius: 999, padding: '7px 18px', cursor: 'pointer',
          color: '#1A1A1A', fontSize: 12, fontWeight: 400, letterSpacing: '0.5px',
        }}
      >
        <i className="ri-arrow-left-line" style={{ fontSize: 14 }} />
        Gallery Overview
      </button>

      <div className="flex items-center gap-2">
        {(['main', 'north', 'east', 'south', 'west'] as WingKey[]).map(w => {
          const wc = wingConfig[w];
          const active = w === currentWing;
          return (
            <button key={w} onClick={() => onNavigateWing(w)}
              className="flex items-center gap-2 whitespace-nowrap transition-all hover:bg-black/5"
              style={{
                padding: '7px 16px', borderRadius: 999,
                border: active ? '1px solid #1A1A1A' : '1px solid rgba(0,0,0,0.1)',
                background: active ? 'rgba(0,0,0,0.05)' : 'transparent',
                cursor: 'pointer',
                color: active ? '#1A1A1A' : '#666666',
                fontSize: 11, fontWeight: active ? 500 : 400, letterSpacing: '0.5px',
              }}
            >
              {wc.name}
            </button>
          );
        })}
      </div>

      <div style={{ color: '#AAAAAA', fontSize: 10, letterSpacing: '1px' }}>
        ← → or drag to pan
      </div>
    </div>
  );
}

// ─── Main WingRoom ────────────────────────────────────────────────────────────
export default function WingRoom({ wing, artworks, lightIntensity, onArtworkClick, onNavigateWing }: WingRoomProps) {
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isEntering, setIsEntering] = useState(true);
  const [walkingForward, setWalkingForward] = useState(false);
  const [walkDirection, setWalkDirection] = useState<'prev' | 'next' | null>(null);
  const [zoomArtwork, setZoomArtwork] = useState<Artwork | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    setIsEntering(true);
    setWalkingForward(false);
    setWalkDirection(null);
    setZoomArtwork(null);
    setShowDetail(false);
    const t = setTimeout(() => setIsEntering(false), 900);
    return () => clearTimeout(t);
  }, [wing]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (showDetail || zoomArtwork !== null) return;
      if (e.key === 'ArrowLeft') doNavigate('prev');
      else if (e.key === 'ArrowRight') doNavigate('next');
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [wing, isTransitioning, showDetail, zoomArtwork]);

  const idx = wingOrder.indexOf(wing);
  const cfg = wingConfig[wing];
  const prevWing = wingOrder[(idx - 1 + wingOrder.length) % wingOrder.length];
  const nextWing = wingOrder[(idx + 1) % wingOrder.length];
  const lightMult = lightIntensity / 100;

  const doNavigate = (dir: 'prev' | 'next') => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setWalkingForward(true);
    setWalkDirection(dir);
    const target = dir === 'next' ? nextWing : prevWing;
    setTimeout(() => {
      onNavigateWing(target);
      setTimeout(() => setIsTransitioning(false), 100);
    }, 900);
  };

  const handlePaintingClick = (artwork: Artwork) => {
    setZoomArtwork(artwork);
    setShowDetail(false);
    onArtworkClick(artwork.id);
  };

  const handleZoomComplete = () => {
    setZoomArtwork(null);
    setShowDetail(true);
    setSelectedArtwork(zoomArtwork);
  };

  return (
    <div className="relative w-full h-screen overflow-hidden" style={{ background: '#F5F5F5' }}>

      {/* Walk-forward white flash */}
      <div
        className="fixed inset-0 bg-white pointer-events-none"
        style={{
          zIndex: 50,
          opacity: walkingForward ? 1 : 0,
          transition: walkingForward ? 'opacity 0.35s ease 0.6s' : 'opacity 0.55s ease',
        }}
      />

      {/* Enter animation */}
      <div className="w-full h-full" style={{
        opacity: isEntering ? 0 : 1,
        transform: isEntering ? 'scale(1.06)' : 'scale(1)',
        transition: 'opacity 0.9s ease, transform 0.9s ease',
      }}>
        <GalleryRoomView
          artworks={artworks}
          lightMult={lightMult}
          onPaintingClick={handlePaintingClick}
          onNavigateNext={() => doNavigate('next')}
          onNavigatePrev={() => doNavigate('prev')}
          nextWing={nextWing}
          prevWing={prevWing}
          walkingForward={walkingForward}
          walkDirection={walkDirection}
        />
      </div>

      {/* Top nav */}
      <TopNavBar currentWing={wing} onNavigateWing={onNavigateWing} onBackToGallery={() => onNavigateWing('main' as WingKey)} />

      {/* Bottom info bar */}
      <div className="absolute flex items-center gap-4" style={{
        bottom: 24, left: '50%', transform: 'translateX(-50%)',
        zIndex: 40,
        background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)',
        padding: '10px 28px', borderRadius: 999,
        border: '1px solid rgba(0,0,0,0.1)',
      }}>
        <i className="ri-gallery-line" style={{ color: '#666', fontSize: 14 }} />
        <span className="whitespace-nowrap" style={{ color: '#1A1A1A', fontSize: 13, fontWeight: 500, letterSpacing: '1px', textTransform: 'uppercase' }}>
          {cfg.name}
        </span>
        <span style={{ color: '#CCC' }}>·</span>
        <span className="whitespace-nowrap" style={{ color: '#666', fontSize: 11 }}>
          {artworks.length} artwork{artworks.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Walk-up zoom animation */}
      {zoomArtwork && (
        <WalkUpZoom artwork={zoomArtwork} onComplete={handleZoomComplete} />
      )}

      {/* Artwork Detail Overlay */}
      {showDetail && selectedArtwork && (
        <InformationOverlay
          artworkId={selectedArtwork.id}
          artworks={artworks}
          onClose={() => { setShowDetail(false); setSelectedArtwork(null); }}
          onNavigate={(id) => {
            const found = artworks.find(a => a.id === id) ?? null;
            setSelectedArtwork(found);
          }}
        />
      )}
    </div>
  );
}
